import { formatAED, localDate, shiftDateKey, studioNumbers, type OwnerHome, type OwnerHomePerson, type OwnerHomeRunningOut, type StudioNumberKey } from "@teamna/shared";
import type { Words } from "../families/model.ts";

/**
 * The owner Home, as words and numbers (2026-10-04). Pure, so the sentences are tested without a
 * screen: the greeting, the line under it, each tile's number and the line under the number.
 */

/** The tiles of a family business, then the five studio numbers (H4) where clients book for themselves. */
export type FamilyTileKey = "participants" | "came" | "booked" | "quiet" | "run";
export type TileKey = FamilyTileKey | StudioNumberKey;

export function greeting(now: Date, timezone: string): string {
  const hour = Number(new Intl.DateTimeFormat("en-GB", { hour: "numeric", hour12: false, timeZone: timezone }).format(now));
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** What changed from the week before, in words. */
export function cameDelta(home: OwnerHome): string {
  const diff = home.came.lastWeek - home.came.weekBefore;
  if (diff === 0) return "Same as the week before";
  return `${Math.abs(diff)} ${diff > 0 ? "more" : "fewer"} than the week before`;
}

/** Every participant, as the Swimmers page counts them: with a parent, and the names that wait for one. */
export function participantTotal(home: OwnerHome): number {
  return home.participants.all.length + (home.participants.waiting?.length ?? 0);
}

export function quietToAsk(home: OwnerHome) {
  return home.quiet.filter((row) => !row.nudge);
}

/**
 * Everything overdue, for every family: the same rule as the AI Assistant (decision 2026-10-23). An
 * older API build sends no `overdue`: then only the families running out.
 */
export function overdueTotal(home: OwnerHome): { totalFils: number; families: number } {
  return home.overdue ?? { totalFils: owedTotal(home), families: owingFamilies(home) };
}

/**
 * The rows with sessions and money: Running out where members book for their children, Renewals
 * (H3) where clients book for themselves. The server fills one of the two.
 */
export function moneyRows(home: OwnerHome): OwnerHomeRunningOut[] {
  return [...home.runningOut, ...(home.renewals ?? [])];
}

/** Overdue from the families running out (or the clients to renew) only. */
export function owedTotal(home: OwnerHome): number {
  return moneyRows(home).reduce((sum, row) => sum + (row.owed?.dueFils ?? 0), 0);
}

export function owingFamilies(home: OwnerHome): number {
  return new Set(moneyRows(home).filter((row) => row.owed && row.owed.dueFils > 0).map((row) => row.person.familyId)).size;
}

/** The line under the greeting: the week in one sentence. */
export function heroSentence(home: OwnerHome, words: Words): string {
  const total = participantTotal(home);
  // The first day (F2): a family business adds families, a studio adds its clients.
  if (total === 0) return words.forChildren ? `Add your families and their ${words.participants}.` : `Add your ${words.members}.`;
  const parts = [`${home.came.lastWeek} of ${plural(total, words.participant, words.participants)} came last week`];
  const diff = home.came.lastWeek - home.came.weekBefore;
  if (diff !== 0) parts[0] += `, ${Math.abs(diff)} ${diff > 0 ? "more" : "fewer"} than the week before`;
  const ask = quietToAsk(home).length;
  const run = home.runningOut.length;
  const renew = home.renewals?.filter((row) => !row.nudge).length ?? 0;
  const tail: string[] = [];
  if (ask) tail.push(`${ask} ${ask === 1 ? "needs" : "need"} a message`);
  if (run) tail.push(`${run} ${run === 1 ? "is" : "are"} running out of sessions`);
  if (renew) tail.push(`${renew} ${renew === 1 ? "needs" : "need"} a renewal`);
  return tail.length ? `${parts[0]}. ${tail.join(", and ")}.` : `${parts[0]}. Nobody needs a message today.`;
}

export type Tile = { key: TileKey; label: string; value: number | string; unit: string; delta: string; tone: "up" | "down" | "flat" };

/**
 * The tile set comes from the shape. Where clients book for themselves the server sends the five
 * studio numbers (H4: fill this week, active clients, ending soon, intro to package, late cancels),
 * and the Renewals card (H3) takes the place of Running out. A family business keeps its five tiles.
 * An older API build sends no `studio`: then the family tiles, without Running out when the
 * Renewals list is there.
 */
export function tiles(home: OwnerHome, words: Words): Tile[] {
  if (home.studio) {
    return studioNumbers(home.studio, words).map((number) => ({
      key: number.key,
      label: number.label,
      value: number.value,
      unit: number.unit,
      delta: number.note,
      tone: "flat",
    }));
  }
  return familyTiles(home, words).filter((tile) => !(tile.key === "run" && home.renewals));
}

/** The five tiles of a business where members book for their children. */
function familyTiles(home: OwnerHome, words: Words): Tile[] {
  const total = participantTotal(home);
  const net = home.participants.joinedThisMonth.length - home.participants.leftThisMonth.length;
  const ask = quietToAsk(home);
  const owed = overdueTotal(home).totalFils;
  const longest = ask[0];
  return [
    {
      key: "participants",
      label: capitalFirst(words.participants),
      value: total,
      unit: `in ${plural(home.participants.groups, words.group, words.groups)}`,
      delta: net === 0 ? "No change this month" : `${net > 0 ? "+" : ""}${net} this month`,
      tone: net > 0 ? "up" : net < 0 ? "down" : "flat",
    },
    {
      key: "came",
      label: "Came last week",
      value: home.came.lastWeek,
      unit: `of ${total}`,
      delta: cameDelta(home),
      tone: home.came.lastWeek > home.came.weekBefore ? "up" : home.came.lastWeek < home.came.weekBefore ? "down" : "flat",
    },
    {
      key: "booked",
      label: "Booked this week",
      value: home.booked.booked,
      unit: `of ${plural(home.booked.seats, "seat", "seats")}`,
      delta: home.booked.seats ? `${plural(home.booked.seats - home.booked.booked, "seat", "seats")} still empty` : "No sessions this week",
      tone: "flat",
    },
    {
      key: "quiet",
      label: "Not seen in 2 weeks",
      value: ask.length,
      unit: words.participants,
      delta: longest ? `Longest: ${longest.person.name}, ${longest.daysSince} days` : home.quiet.length ? "Everyone has been asked" : "Nobody to ask",
      tone: "flat",
    },
    {
      key: "run",
      label: "Running out",
      value: home.runningOut.length,
      unit: words.participants,
      delta: owed ? `${formatAED(owed)} overdue` : "Nothing overdue",
      tone: owed ? "down" : "flat",
    },
  ];
}

export function capitalFirst(word: string): string {
  return word ? word[0]!.toUpperCase() + word.slice(1) : word;
}

/** "3 days ago", "Yesterday", "Not yet". */
export function lastCameText(person: Pick<OwnerHomePerson, "lastCameAt">, now: Date): string {
  if (!person.lastCameAt) return "Not yet";
  const days = Math.floor((now.getTime() - new Date(person.lastCameAt).getTime()) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
}

/** "Sat 4 Oct": a short date in the business timezone. */
export function shortDate(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: timezone }).format(new Date(iso));
}

/** "Today, 4 Oct", "Yesterday, 3 Oct", "Thursday, 2 Oct": the days a payment can be recorded for. */
export function paymentDays(now: Date, timezone: string): Array<{ key: string; label: string }> {
  const today = localDate(now, timezone);
  return [0, 1, 2].map((back) => {
    const key = shiftDateKey(today, -back);
    const date = new Date(`${key}T12:00:00.000Z`);
    const dayMonth = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }).format(date);
    const name = back === 0 ? "Today" : back === 1 ? "Yesterday" : new Intl.DateTimeFormat("en-GB", { weekday: "long", timeZone: "UTC" }).format(date);
    return { key, label: `${name}, ${dayMonth}` };
  });
}

/** "AED 480": typed text to fils. Commas and the currency are ignored. */
export function parseAmount(text: string): number {
  const cleaned = text.replace(/[^\d.]/g, "");
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) && value > 0 ? Math.round(value * 100) : 0;
}

/** "Paid in full", or how much stays due after this payment. */
export function paymentSummary(amountFils: number, dueFils: number): { text: string; full: boolean; tooMuch: boolean } {
  if (!amountFils) return { text: "Type the amount you received.", full: false, tooMuch: false };
  if (amountFils > dueFils) return { text: `That is more than is due. The most is ${formatAED(dueFils)}.`, full: false, tooMuch: true };
  if (amountFils === dueFils) return { text: "Paid in full.", full: true, tooMuch: false };
  return { text: `Part payment. ${formatAED(dueFils - amountFils)} will still be due.`, full: false, tooMuch: false };
}
